import type { EmailTemplate } from '@/features/email-marketing';
import type { PlatformConfig, SystemEmail, SystemPush } from '@/features/platform/model/types';
import { DEFAULT_SURVEY_BLOCK } from '@/features/platform/model/survey-block';

/**
 * Fotografia do que o legado manda hoje, com o texto que está no código
 * (`services/mailerSend.js`, `controllers/user.js`, `services/expirePointsNovo.js`,
 * `services/mgmNotify.js`). Nada disto chega ao servidor: é a demonstração
 * até a API de plataforma existir (ver docs/plataforma-integracao.md).
 */

/**
 * Casca do e-mail de sistema, igual à que `services/mailerSend.js` monta no
 * legado (e-mail de transação): fundo e texto nas cores do passbook, logo da
 * loja em cima, folha branca, opt-out "Privacidade e Segurança" e o bloco de
 * ajuda no WhatsApp. As cores e o logo entram como variáveis porque mudam por
 * loja; a prévia preenche com `VARIABLE_SAMPLES`.
 */
function systemHtml(
  title: string,
  body: string,
  cta?: { label: string; href: string },
  options: { survey?: boolean } = {},
): string {
  const cell =
    'border-collapse:collapse;border-spacing:0;margin:0;padding:0 6.25%;width:87.5%;font-family:sans-serif;color:#000000;text-align:center';
  const link = 'color:#127DB3;font-family:sans-serif;font-size:17px;font-weight:400;line-height:160%';
  const rule =
    '<hr color="#E0E0E0" align="center" width="100%" size="1" noshade style="margin:0;padding:0">';
  return `<!doctype html><html><head><meta charset="utf-8"></head>
<body bgcolor="{{cor_fundo}}" style="margin:0;padding:0;width:100%;background-color:{{cor_fundo}};color:{{cor_texto}}">
<table role="presentation" width="100%" align="center" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%"><tr><td align="center" valign="top" bgcolor="{{cor_fundo}}" style="padding:0;background-color:{{cor_fundo}}">
<table role="presentation" width="560" align="center" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:inherit;max-width:560px">
<tr><td align="center" style="padding:20px 6.25%"><img src="{{logo}}" alt="{{loja}}" width="350" style="width:350px;max-width:100%;border:0;display:block;margin:0 auto"></td></tr>
</table>
<table role="presentation" width="560" align="center" bgcolor="#FFFFFF" cellpadding="0" cellspacing="0" style="border-collapse:separate;border-spacing:0;width:inherit;max-width:560px;background:#ffffff;border-radius:8px;overflow:hidden">
<tr><td align="center" valign="top" style="${cell};padding-top:25px;font-size:24px;font-weight:bold;line-height:130%">${title}</td></tr>
<tr><td align="center" valign="top" style="${cell};padding-top:25px;font-size:20px;font-weight:400;line-height:160%">${body}</td></tr>${
    cta
      ? `
<tr><td align="center" valign="top" style="${cell};padding-top:25px"><a href="${cta.href}" style="display:inline-block;background:#34c38f;color:#ffffff;padding:10px 20px;border-radius:5px;font-family:sans-serif;font-size:17px;font-weight:bold;text-decoration:none">${cta.label}</a></td></tr>`
      : ''
  }
<tr><td align="center" valign="top" style="${cell};padding-top:25px">${rule}</td></tr>${
    options.survey
      ? `
<tr><td align="center" valign="top" style="${cell}">{{bloco_pesquisa}}</td></tr>`
      : ''
  }
<tr><td align="center" valign="top" style="${cell};padding-top:20px;padding-bottom:25px;font-size:17px;font-weight:400;line-height:160%">⚠️ Não quero mais receber mensagens (clique no link abaixo)<br><a href="{{link_optout}}" target="_blank" style="${link}">Privacidade e Segurança</a></td></tr>
<tr><td align="center" valign="top" style="${cell}">${rule}</td></tr>
<tr><td align="center" valign="top" style="${cell};padding-top:20px;padding-bottom:25px;font-size:17px;font-weight:400;line-height:160%">Precisa de ajuda? Clique no link abaixo e fale com nosso time de atendimento :)<br><a href="{{link_ajuda}}" target="_blank" style="${link}">Iniciar conversa</a></td></tr>
</table>
</td></tr></table></body></html>`;
}

function template(input: Omit<EmailTemplate, 'editor' | 'origin' | 'blocks' | 'beefreeJson' | 'plainText' | 'tags' | 'domain'>): EmailTemplate {
  return {
    ...input,
    editor: 'html',
    origin: 'local',
    blocks: [],
    beefreeJson: '',
    plainText: input.html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
    tags: ['sistema'],
    domain: 'fixecrm.com.br',
  };
}

export const PLATFORM_EMAIL_TEMPLATES: EmailTemplate[] = [
  template({
    id: 'sys-transacao',
    name: 'Transação',
    category: 'Aviso',
    description: 'Pontos, selos ou cashback que mudaram na carteira.',
    subject: 'Programa de fidelidade: {{titulo}}',
    previewText: '{{mensagem}}',
    // Pesquisa: o legado só insere o bloco quando a loja tem `regraCartao.hasSurvey`.
    html: systemHtml('{{titulo}}', '{{mensagem}}', undefined, { survey: true }),
    updatedAt: '2026-10-27T09:00:00-03:00',
  }),
  template({
    id: 'sys-convite',
    name: 'Convite do cartão',
    category: 'Relacionamento',
    description: 'Link para o participante adicionar o cartão na carteira.',
    subject: 'Seu cartão {{loja}}',
    previewText: 'Adicione o cartão à sua carteira',
    html: systemHtml('Olá, {{primeiro_nome}}', 'Seu cartão {{loja}} está pronto. Toque no botão para adicionar à carteira.', {
      label: 'Adicionar cartão',
      href: '{{link}}',
    }),
    updatedAt: '2026-10-27T09:00:00-03:00',
  }),
  template({
    id: 'sys-codigo',
    name: 'Código do app',
    category: 'Aviso',
    description: 'Código de acesso pedido pelo aplicativo.',
    subject: 'Seu código {{loja}}',
    previewText: 'Código {{codigo}}',
    html: systemHtml('Seu código de acesso', '<strong style="font-size:28px;letter-spacing:4px">{{codigo}}</strong>'),
    updatedAt: '2026-10-27T09:00:00-03:00',
  }),
  template({
    id: 'sys-android',
    name: 'Cartão no Android',
    category: 'Relacionamento',
    description: 'Link do cartão para quem está no Android.',
    subject: 'Seu cartão {{loja}} no Android',
    previewText: 'Abra o link no celular',
    html: systemHtml('Seu cartão no Android', 'Abra o link no celular para guardar o cartão {{loja}}.', { label: 'Abrir cartão', href: '{{link}}' }),
    updatedAt: '2026-10-27T09:00:00-03:00',
  }),
  template({
    id: 'sys-export',
    name: 'Exportação',
    category: 'Aviso',
    description: 'Arquivo exportado pelo painel, pronto para baixar.',
    subject: '{{assunto}}',
    previewText: '{{arquivo}}',
    html: systemHtml('Sua exportação está pronta', 'Arquivo: {{arquivo}}', { label: 'Baixar', href: '{{link}}' }),
    updatedAt: '2026-10-27T09:00:00-03:00',
  }),
];

export const SYSTEM_EMAILS: SystemEmail[] = [
  {
    kind: 'transaction',
    label: 'Transação',
    trigger: 'Pontos, selos ou cashback mudam na carteira',
    subject: { pt: 'Programa de fidelidade: {{titulo}}', es: 'Programa de fidelización: {{titulo}}', en: 'Loyalty program: {{titulo}}' },
    templateId: 'sys-transacao',
    variables: ['loja', 'titulo', 'mensagem', 'cor_fundo', 'cor_texto', 'logo', 'link_optout', 'link_ajuda', 'bloco_pesquisa', 'link_pesquisa'],
    surveyBlock: { ...DEFAULT_SURVEY_BLOCK },
    enabled: true,
    legacy: 'mailerSend.enviarTransacaoPorEmail',
  },
  {
    kind: 'invite',
    label: 'Convite do cartão',
    trigger: 'Cadastro novo ou reenvio do cartão',
    subject: { pt: 'Seu cartão {{loja}}', es: 'Tu tarjeta {{loja}}', en: 'Your {{loja}} card' },
    templateId: 'sys-convite',
    variables: ['loja', 'nome', 'primeiro_nome', 'link', 'cor_fundo', 'cor_texto', 'logo', 'link_optout', 'link_ajuda'],
    enabled: true,
    legacy: 'mailerSend.enviarInvite',
  },
  {
    kind: 'app-code',
    label: 'Código do app',
    trigger: 'Aplicativo pede código de acesso',
    subject: { pt: 'Seu código {{loja}}', es: 'Tu código {{loja}}', en: 'Your {{loja}} code' },
    templateId: 'sys-codigo',
    variables: ['loja', 'codigo', 'cor_fundo', 'cor_texto', 'logo', 'link_optout', 'link_ajuda'],
    enabled: true,
    legacy: 'mailerSend.enviarTokenAPP',
  },
  {
    kind: 'android-card',
    label: 'Cartão no Android',
    trigger: 'Participante Android pede o cartão por e-mail',
    subject: { pt: 'Seu cartão {{loja}} no Android', es: 'Tu tarjeta {{loja}} en Android', en: 'Your {{loja}} card on Android' },
    templateId: 'sys-android',
    variables: ['loja', 'link', 'cor_fundo', 'cor_texto', 'logo', 'link_optout', 'link_ajuda'],
    enabled: true,
    legacy: 'mailerSend.enviarEmailAndroid',
  },
  {
    kind: 'export',
    label: 'Exportação',
    trigger: 'Painel exporta a base por e-mail',
    subject: { pt: '{{assunto}}', es: '{{assunto}}', en: '{{assunto}}' },
    templateId: null,
    variables: ['loja', 'assunto', 'arquivo', 'link'],
    enabled: true,
    legacy: 'mailerSend.enviarArquivo',
  },
];

const title = { pt: '{{loja}}', es: '{{loja}}', en: '{{loja}}' };

export const SYSTEM_PUSHES: SystemPush[] = [
  {
    kind: 'points-added',
    label: 'Pontos adicionados',
    trigger: 'Venda ou integração credita pontos ou cashback',
    title,
    body: {
      pt: '{{quantidade}} {{unidade}} adicionados à sua carteira {{loja}}',
      es: 'Se agregaron {{quantidade}} {{unidade}} a tu monedero de {{loja}}.',
      en: '{{quantidade}} {{unidade}} were added to your {{loja}} wallet.',
    },
    variables: ['loja', 'quantidade', 'unidade', 'saldo'],
    enabled: true,
    legacy: 'controllers/user.js (transaction)',
  },
  {
    kind: 'stamps-added',
    label: 'Selos adicionados',
    trigger: 'Venda ou operador credita selos',
    title,
    body: {
      pt: '{{quantidade}} selos foram adicionados à sua carteira {{loja}}',
      es: 'Se agregaron {{quantidade}} sellos a tu monedero de {{loja}}.',
      en: '{{quantidade}} stamps were added to your {{loja}} wallet.',
    },
    variables: ['loja', 'quantidade', 'saldo'],
    enabled: true,
    legacy: 'controllers/user.js (transaction)',
  },
  {
    kind: 'redeem',
    label: 'Resgate',
    trigger: 'Participante resgata uma recompensa',
    title,
    body: {
      pt: 'Resgate na sua carteira {{loja}} feito com sucesso.',
      es: 'Canjea tu recompensa de {{loja}} con éxito en tu billetera.',
      en: 'Your reward from {{loja}} was successfully redeemed in your wallet.',
    },
    variables: ['loja', 'saldo'],
    enabled: true,
    legacy: 'controllers/user.js (transaction)',
  },
  {
    kind: 'purchase-cancelled',
    label: 'Compra cancelada',
    trigger: 'Venda cancelada estorna o crédito',
    title,
    body: { pt: 'Compra cancelada da sua carteira {{loja}}', es: 'Compra cancelada de tu monedero {{loja}}', en: 'Purchase cancelled from your {{loja}} wallet' },
    variables: ['loja', 'quantidade', 'unidade'],
    enabled: true,
    legacy: 'controllers/user.js (cancel)',
  },
  {
    kind: 'profile-updated',
    label: 'Cadastro atualizado',
    trigger: 'Participante altera os dados do cadastro',
    title,
    body: { pt: 'Cadastro atualizado com sucesso', es: 'Registro actualizado con éxito', en: 'Profile updated successfully' },
    variables: ['loja'],
    enabled: true,
    legacy: 'controllers/user.js (profile)',
  },
  {
    kind: 'expiring-soon',
    label: 'Perto de expirar',
    trigger: 'Saldo vence em N dias',
    title,
    body: {
      pt: 'Seus {{unidade}} estão perto de expirar. Aproveite para usar na {{loja}}.',
      es: 'Tus {{unidade}} están por vencer. Aprovecha para usarlos en {{loja}}.',
      en: 'Your {{unidade}} are about to expire. Use them at {{loja}}.',
    },
    variables: ['loja', 'unidade', 'quantidade', 'dias'],
    rule: { daysBefore: 15 },
    enabled: true,
    legacy: 'services/expirePointsNovo.js (aviso)',
  },
  {
    kind: 'expired',
    label: 'Expirado',
    trigger: 'Saldo vencido é removido',
    title,
    body: {
      pt: '{{quantidade}} {{unidade}} removidos (expirados) na sua carteira {{loja}}',
      es: '{{quantidade}} {{unidade}} eliminados (vencidos) de tu monedero {{loja}}',
      en: '{{quantidade}} {{unidade}} removed (expired) from your {{loja}} wallet',
    },
    variables: ['loja', 'quantidade', 'unidade'],
    enabled: true,
    legacy: 'services/expirePointsNovo.js (expirar)',
  },
  {
    kind: 'nps-bonus',
    label: 'Bônus da pesquisa',
    trigger: 'Participante responde a pesquisa com bonificação',
    title,
    body: {
      pt: 'Você ganhou {{quantidade}} {{unidade}} por responder a pesquisa.',
      es: 'Ganaste {{quantidade}} {{unidade}} por responder la encuesta.',
      en: 'You earned {{quantidade}} {{unidade}} for answering the survey.',
    },
    variables: ['loja', 'quantidade', 'unidade'],
    enabled: true,
    legacy: 'controllers/user.js (NPS)',
  },
  {
    kind: 'mgm-reward',
    label: 'Indicação recompensada',
    trigger: 'Indicado completa a condição do Indique e ganhe',
    title,
    body: {
      pt: 'Sua indicação virou recompensa: {{quantidade}} {{unidade}} na carteira {{loja}}.',
      es: 'Tu recomendación se convirtió en recompensa: {{quantidade}} {{unidade}}.',
      en: 'Your referral became a reward: {{quantidade}} {{unidade}}.',
    },
    variables: ['loja', 'quantidade', 'unidade', 'nome'],
    enabled: true,
    legacy: 'services/mgmNotify.js',
  },
  {
    kind: 'coupon',
    label: 'Cupom liberado',
    trigger: 'Cupom validado fica disponível no site',
    title,
    body: {
      pt: 'Clique no link e gere seu cupom pelo site.',
      es: 'Haz clic en el enlace y genera tu cupón en el sitio.',
      en: 'Tap the link to generate your coupon on the website.',
    },
    variables: ['loja', 'link'],
    enabled: true,
    legacy: 'services/notification.js (cupom)',
  },
];

export const PLATFORM_CONFIG: PlatformConfig = {
  emails: SYSTEM_EMAILS,
  pushes: SYSTEM_PUSHES,
  sender: {
    fromEmail: 'fixe@fixecrm.com.br',
    fromName: 'FixeCRM',
    partners: [{ partner: 'goomer', fromEmail: 'goomer@slf.vc', fromName: 'Goomer' }],
  },
};
